/**
 * Public API for the workbook model.
 *
 * Consumers should never reach into ingestion/cache internals; this file
 * is the entire surface. Three calls cover the lifecycle:
 *
 *   - `getWorkbookModel(scope)` — build or return the cached model with
 *     Layer 0 + Layer 1 populated.
 *   - `ensurePatternAnalysis(model)` — populate Layer 2 if not already.
 *   - `ensureGraph(model)` — populate Layer 3 if not already.
 *
 * `invalidateWorkbookModel()` is the explicit cache reset (e.g. when the
 * user clicks Rescan). Implicit invalidation by fingerprint comparison is
 * handled inside `getWorkbookModel`.
 *
 * Phase B wired: `getWorkbookModel` orchestrates buildCatalogs →
 * (per-target) readSheetInChunks → SheetSink → segmentRegions →
 * SheetModel. `ensurePatternAnalysis` and `ensureGraph` are still
 * stubbed pending Phases 6 and 8.
 */

import type { SheetModel, WorkbookModel, ScanScope } from "./types";
import { buildCatalogs, type IngestionTarget } from "./ingestion/catalogs";
import { readSheetInChunks } from "./ingestion/chunkedReader";
import { SheetSink, type SheetSinkOutput } from "./ingestion/sheetSink";
import {
  analyzePatternsInWorker,
  segmentRegionsInWorker,
  type SegmentRegionsResult,
} from "./worker/client";

// Re-export the entire type surface so consumers do `from "../model"`
// and never have to reach into `./types`.
export type {
  WorkbookModel,
  SheetModel,
  SheetBounds,
  Region,
  ScanScope,
  AnomalyType,
  AnomalyRecord,
  PatternAnalysis,
  ReferenceGraph,
  BrokenRef,
  TableEntry,
  NameEntry,
  SpillSheetIndex,
  WorkbookCatalogs,
  OriginValue,
  ValueKindValue,
  NodeKindValue,
} from "./types";

export { Origin, ValueKind, NodeKind } from "./types";

// ============================================================================
// Internal cache
// ============================================================================

/**
 * Single in-memory model, keyed by scope. A separate slot per scope
 * because a workbook-scoped scan and a selection-scoped scan produce
 * meaningfully different `sheets` maps and shouldn't evict each other.
 */
const cache: Partial<Record<ScanScope, WorkbookModel>> = {};

// ============================================================================
// Public API
// ============================================================================

/**
 * Build (or return a cached copy of) the workbook model for the requested
 * scope. Layer 0 + Layer 1 are populated; Layers 2 and 3 are `null` and
 * must be requested explicitly.
 *
 * Re-scans the workbook if no cached model exists or its fingerprint no
 * longer matches the live workbook. The fingerprint is structural —
 * sheet add/rename/resize, table changes, defined-name changes — and
 * does NOT change on cell-value edits. For cell-level invalidation,
 * call `invalidateWorkbookModel(scope)` explicitly.
 *
 * Sequence:
 *   1. Build catalogs (one or two Office.js syncs, cheap).
 *   2. Compare fingerprint to cached model for the same scope; return
 *      cached if match.
 *   3. Otherwise: for each ingestion target, stream chunks through a
 *      SheetSink (Layer 0) and run segmentRegions (Layer 1).
 *   4. Assemble the WorkbookModel, cache it, return.
 *
 * @throws on any Office.js error during ingestion. The cache is left
 *         unchanged on failure (no partial models cached).
 */
export async function getWorkbookModel(scope: ScanScope): Promise<WorkbookModel> {
  const { catalogs, workbookName, fingerprint, ingestionTargets } =
    await buildCatalogs(scope);

  const cached = cache[scope];
  if (cached && cached.fingerprint === fingerprint) return cached;

  // The freshly-built catalogs have empty interning dicts; the sinks
  // populate them in turn as chunks stream in. The map insertion order
  // matches `ingestionTargets`' iteration order (each sheet finalized
  // in the iteration that processes the NEXT sheet's I/O — see the
  // pipelining comment below).
  const sheets = new Map<number, SheetModel>();

  // Pipelining: while sheet N+1's Office.js read happens on the main
  // thread, sheet N's Layer 1 runs on the Worker in parallel. The
  // typed-array transfer means main thread doesn't touch N's
  // cellOrigin/rowDensity/colDensity while the worker has them — and
  // the main thread is busy with the next sheet's Excel.run anyway, so
  // the overlap is real concurrency, not just a queue defer.
  //
  // Per iteration:
  //   1. Read sheet N's chunks via Office.js (await).
  //   2. Kick off sheet N's Layer 1 in the worker (no await).
  //   3. If there's an inflight previous sheet (N-1), await its Layer 1
  //      and assemble its SheetModel.
  //   4. Save (target, layer0, layer1Promise) as the new inflight tail.
  // After the loop ends, drain the last inflight sheet.
  let inflight: {
    target: IngestionTarget;
    layer0: SheetSinkOutput;
    layer1Promise: Promise<SegmentRegionsResult>;
  } | null = null;

  for (const target of ingestionTargets) {
    const sink = new SheetSink(target, catalogs);
    await readSheetInChunks(target, (chunk) => sink.ingest(chunk));
    const layer0 = sink.getOutput();

    // Kick off Layer 1 for this sheet — don't await. The worker takes
    // ownership of the three input buffers for the duration; the
    // SheetSink loses its references either way (we've already called
    // getOutput).
    const layer1Promise = segmentRegionsInWorker(
      layer0.bounds,
      layer0.cellOrigin,
      layer0.rowDensity,
      layer0.colDensity
    );

    // While this sheet's Layer 1 cooks (and concurrently, the next
    // iteration's Office.js read), finalize the previous inflight sheet.
    if (inflight !== null) {
      const prev = inflight;
      const prevLayer1 = await prev.layer1Promise;
      sheets.set(prev.target.sheetId, buildSheetModel(prev.target, prev.layer0, prevLayer1));
    }

    inflight = { target, layer0, layer1Promise };
  }

  // Drain the final inflight sheet after the loop exits.
  if (inflight !== null) {
    const last = await inflight.layer1Promise;
    sheets.set(inflight.target.sheetId, buildSheetModel(inflight.target, inflight.layer0, last));
  }

  const model: WorkbookModel = {
    fingerprint,
    scope,
    workbookName,
    catalogs,
    sheets,
    graph: null,
  };

  cache[scope] = model;
  return model;
}

/**
 * Assemble a SheetModel from the SheetSink's Layer 0 output plus the
 * worker's Layer 1 result. The worker transferred the input arrays
 * (cellOrigin, rowDensity, colDensity) back, so we use the worker's
 * view of them on the model — same data, possibly different
 * ArrayBuffer instances after the round trip.
 */
function buildSheetModel(
  target: IngestionTarget,
  layer0: SheetSinkOutput,
  layer1: SegmentRegionsResult
): SheetModel {
  return {
    sheetId: target.sheetId,
    name: target.name,
    bounds: layer0.bounds,
    cellOrigin: layer1.cellOrigin,
    cellValueKind: layer0.cellValueKind,
    numericGrid: layer0.numericGrid,
    r1c1Ids: layer0.r1c1Ids,
    formulaA1Ids: layer0.formulaA1Ids,
    numFmtIds: layer0.numFmtIds,
    textValueIds: layer0.textValueIds,
    rowDensity: layer1.rowDensity,
    colDensity: layer1.colDensity,
    regions: layer1.regions,
    regionOfCell: layer1.regionOfCell,
    patternAnalysis: null,
  };
}

/**
 * Discard the cached model for one scope (or all scopes if omitted). The
 * next `getWorkbookModel` call for that scope will trigger a fresh scan.
 */
export function invalidateWorkbookModel(scope?: ScanScope): void {
  if (scope) {
    delete cache[scope];
  } else {
    (Object.keys(cache) as ScanScope[]).forEach((k) => delete cache[k]);
  }
}

/**
 * Populate `model.sheets.get(*).patternAnalysis` for every scanned sheet
 * that doesn't already have it. Runs Layer 2 (formula pattern grouping +
 * anomaly detection) in the analysis Worker; the sheet's cellOrigin,
 * cellValueKind, and r1c1Ids arrays are transferred to the worker and
 * transferred back, with the SheetModel re-linked to the returned
 * buffers so rules still have access.
 *
 * Idempotent: sheets whose `patternAnalysis` is already populated are
 * skipped. Mutates the model in place so subsequent calls share the
 * computed analysis without redoing the work.
 */
export async function ensurePatternAnalysis(model: WorkbookModel): Promise<void> {
  for (const sheet of Array.from(model.sheets.values())) {
    if (sheet.patternAnalysis !== null) continue;

    const result = await analyzePatternsInWorker(
      sheet.bounds,
      sheet.regions,
      sheet.cellOrigin,
      sheet.cellValueKind,
      sheet.r1c1Ids
    );

    // Re-link the transferred-back typed arrays. Same data, but the
    // underlying ArrayBuffer instances may differ after the round trip.
    sheet.cellOrigin = result.cellOrigin;
    sheet.cellValueKind = result.cellValueKind;
    sheet.r1c1Ids = result.r1c1Ids;
    sheet.patternAnalysis = {
      anomalyMask: result.anomalyMask,
      anomalies: result.anomalies,
    };
  }
}

/**
 * Populate `model.graph`, if not already present. Tokenizes every formula
 * across all scanned sheets and builds the workbook-wide reference graph:
 * CSR adjacency + Tarjan SCC + broken-ref list. No Office.js calls.
 *
 * Idempotent: returns immediately if `model.graph` is already set.
 */
export async function ensureGraph(model: WorkbookModel): Promise<void> {
  // PHASE 8 — replace with: tokenizer + edge build + CSR + Tarjan.
  void model;
  throw new Error("ensureGraph: not yet implemented (Phase 8)");
}
